/**
 * Care dashboard: resolve recipient, load appointments for recipient, build alerts.
 */
import { Platform } from 'react-native';
import type { CareRole } from './unifiedAuthService';
import type { User } from './unifiedAuthService';
import type { CareMedication } from '../types/care';
import type { CareTask } from '../types/care';
import type { CareAlert } from '../types/care';
import { careMedicationService } from './careMedicationService';
import { careTaskService } from './careTaskService';
import { appointmentManager } from '../utils/appointmentManager';
import type { Appointment } from '../utils/appointmentManager';

/** Resolve care recipient id: senior = self, family/pro = linked or null. */
export function getCareRecipientId(currentUser: User, careRole: CareRole): string | null {
  if (careRole === 'senior') return currentUser.id;
  return currentUser.linkedCareRecipientId ?? null;
}

/** Fetch minimal user by id (for linked recipient name/email). */
export async function getCareRecipientUser(recipientId: string): Promise<{ id: string; name: string; email: string } | null> {
  try {
    if (Platform.OS === 'web') {
      const { doc, getDoc } = await import('firebase/firestore');
      const { getWebDb, initializeFirebase } = await import('./unifiedAuthService');
      if (!initializeFirebase()) return null;
      const db = getWebDb();
      if (!db) return null;
      const snap = await getDoc(doc(db, 'users', recipientId));
      if (!snap.exists()) return null;
      const d = snap.data() as any;
      return { id: recipientId, name: d.name || '', email: d.email || '' };
    }
    const { initializeFirebase, getMobileFirestore } = require('./unifiedAuthService');
    if (!initializeFirebase()) return null;
    const getFs = getMobileFirestore();
    if (!getFs) return null;
    const snap = await getFs().collection('users').doc(recipientId).get();
    if (!snap.exists) return null;
    const d = snap.data() as any;
    return { id: recipientId, name: d.name || '', email: d.email || '' };
  } catch (e) {
    console.warn('getCareRecipientUser error:', e);
    return null;
  }
}

/** Upcoming appointments for the care recipient (filter by email/name). */
export async function getUpcomingAppointmentsForRecipient(
  recipientEmail: string,
  recipientName: string,
  recipientUsername?: string
): Promise<Appointment[]> {
  const all = await appointmentManager.getAppointments();
  const today = new Date().toISOString().slice(0, 10);
  const filtered = (all || []).filter((apt) => {
    const emailMatch = (apt.email || '').toLowerCase() === (recipientEmail || '').toLowerCase();
    const nameMatch = apt.customerName === recipientName || apt.email === recipientUsername;
    return (emailMatch || nameMatch) && apt.appointmentDate >= today && apt.status !== 'cancelled';
  });
  return filtered.sort((a, b) => `${a.appointmentDate}T${a.appointmentTime || '00:00'}`.localeCompare(`${b.appointmentDate}T${b.appointmentTime || '00:00'}`)).slice(0, 10);
}

/** Build alerts from missed meds and overdue tasks. */
export function buildCareAlerts(
  meds: CareMedication[],
  tasks: CareTask[],
  todayDate: string
): CareAlert[] {
  const alerts: CareAlert[] = [];
  meds.forEach((m) => {
    if (careMedicationService.isMissedToday(m, todayDate)) {
      alerts.push({
        id: `alert_med_${m.id}`,
        type: 'missed_med',
        title: 'Missed medication',
        message: `${m.name} was not marked as taken.`,
        relatedId: m.id,
        createdAt: new Date().toISOString(),
      });
    }
  });
  const overdue = careTaskService.getOverdue(tasks, todayDate);
  overdue.forEach((t) => {
    alerts.push({
      id: `alert_task_${t.id}`,
      type: 'overdue_task',
      title: 'Overdue task',
      message: t.title,
      relatedId: t.id,
      createdAt: new Date().toISOString(),
    });
  });
  return alerts.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
